import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  brokerQuotes,
  policyholderAuctioneers,
  policyholderComplement,
  policyholders,
  type DocumentCheck,
  type IndividualQuoteOptions,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatCpfCnpj } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';

import {
  CamposDoLeiloeiro,
  DadosDoLeiloeiro,
} from '../../policyholders/DadosDoLeiloeiro';
import { useUfs } from '../../policyholders/leiloeiro';
import { fieldErrorOf } from '../../shared/fieldError';

import {
  avisoDoCpf,
  avisoDoTelefone,
  digitosDoCpf,
  type RascunhoPf,
} from './cotacaoPessoaFisica';
import { avisosDoPrazo } from './rascunho';

/** O que todo passo da pessoa fisica recebe. */
export interface PassoPfProps {
  rascunho: RascunhoPf;
  alterar: (parcial: Partial<RascunhoPf>) => void;
}

/** Lista das pendencias, com o codigo de cada uma. */
export function ListaDePendencias({
  opcoes,
  codigos,
}: {
  opcoes: IndividualQuoteOptions;
  codigos?: string[];
}) {
  const regras = codigos
    ? opcoes.pending_rules.filter((regra) => codigos.includes(regra.code))
    : opcoes.pending_rules;
  return (
    <ul className="mt-1 list-disc pl-5">
      {regras.map((regra) => (
        <li key={regra.code}>
          <span className="font-medium">{regra.code}</span>: {regra.description}
        </li>
      ))}
    </ul>
  );
}

/**
 * Passo "Dados do tomador" da pessoa fisica (NCT-185 a NCT-198): o CPF do tomador, a
 * busca (ValidatePolicyHolderCpf e SearchPolicyHolderByCPF do OnPoint) e, quando o CPF
 * nao esta cadastrado, o cadastro com nome completo, telefone, e-mail e os dados do
 * leiloeiro. Os documentos para cadastro e o limite do tomador pessoa fisica sao regra
 * pendente.
 */
export function TomadorPfStep({
  rascunho,
  alterar,
  opcoes,
}: PassoPfProps & { opcoes: IndividualQuoteOptions }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [cpf, setCpf] = useState(rascunho.policyholder_document);
  const [conferido, setConferido] = useState<DocumentCheck | null>(null);
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [email, setEmail] = useState('');
  const [uf, setUf] = useState('');
  const [matricula, setMatricula] = useState('');
  const [tentouCadastrar, setTentouCadastrar] = useState(false);
  const [erroDoLeiloeiro, setErroDoLeiloeiro] = useState<string | null>(null);
  // Os dados do leiloeiro sao gravados pela alteracao do tomador (PUT
  // /policyholders/{id}/auctioneer, tomadores.tomador.update). Sem ela, os campos nao
  // aparecem: o perfil so com o cadastro perderia o que digitou (R5-M17).
  const podeGravarLeiloeiro = can('tomadores.tomador.update');

  const buscar = useMutation({
    mutationFn: () => policyholderComplement.validate(digitosDoCpf(cpf)),
    onSuccess: (resultado) => {
      setConferido(resultado);
      setErroDoLeiloeiro(null);
      const proprio =
        resultado.valid &&
        resultado.document_kind === 'cpf' &&
        resultado.registered &&
        resultado.policyholder !== null;
      alterar(
        proprio && resultado.policyholder
          ? {
              policyholder_id: resultado.policyholder.id,
              policyholder_label: resultado.policyholder.label,
              policyholder_document: resultado.document,
            }
          : { policyholder_id: '', policyholder_label: '', policyholder_document: '' },
      );
    },
  });

  // "Prosseguir com o cadastro": o tomador pessoa fisica da corretora e, com a UF, os
  // dados do leiloeiro. O OnPoint mostra "Tomador cadastrado com sucesso. Busque o CPF
  // para prosseguir com a cotacao."; aqui o tomador ja fica escolhido.
  //
  // O cadastro e os dados do leiloeiro sao duas gravacoes. Depois do cadastro gravado,
  // o tomador existe: a falha nos dados do leiloeiro nao pode desfazer a escolha nem
  // dizer que o tomador nao foi cadastrado, porque um novo clique bateria no CPF ja
  // cadastrado. O tomador fica escolhido, a lista e atualizada e o erro do leiloeiro
  // aparece a parte, com os campos para gravar de novo (R5-M17 da revisao 5).
  const cadastrar = useMutation({
    mutationFn: async () => {
      const criado = await policyholders.create({
        legal_name: nome.trim(),
        document: digitosDoCpf(cpf),
        document_kind: 'cpf',
        phone: telefone.trim(),
        email: email.trim(),
      });
      let falhaDoLeiloeiro: string | null = null;
      if (uf !== '' && podeGravarLeiloeiro) {
        try {
          await policyholderAuctioneers.set(criado.id, {
            registry_state: uf,
            registration_number: matricula.trim(),
          });
        } catch (erro) {
          falhaDoLeiloeiro =
            erro instanceof Error
              ? erro.message
              : 'Falha ao gravar os dados do leiloeiro.';
        }
      }
      return { criado, falhaDoLeiloeiro };
    },
    onSuccess: ({ criado, falhaDoLeiloeiro }) => {
      void queryClient.invalidateQueries({ queryKey: ['policyholders'] });
      alterar({
        policyholder_id: criado.id,
        policyholder_label: criado.legal_name,
        policyholder_document: criado.document,
      });
      setConferido(null);
      setErroDoLeiloeiro(falhaDoLeiloeiro);
    },
  });
  const erroDoCadastro = cadastrar.error instanceof ApiError ? cadastrar.error : null;

  const avisoCpf = avisoDoCpf(cpf);
  const naoCadastrado =
    conferido !== null &&
    conferido.valid &&
    conferido.document_kind === 'cpf' &&
    conferido.individual_enabled &&
    !conferido.registered;
  const avisoTelefone = tentouCadastrar ? avisoDoTelefone(telefone) : undefined;
  // A matricula so e gravada com a UF da junta: sem a UF, ela se perderia sem aviso.
  const matriculaSemUf = podeGravarLeiloeiro && matricula.trim() !== '' && uf === '';

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardHeader
          title="Dados do Tomador"
          description="O tomador da cotacao de pessoa fisica e o leiloeiro, pelo CPF."
        />
        <CardBody className="flex flex-col gap-4">
          <form
            className="flex flex-col gap-3 sm:flex-row sm:items-end"
            onSubmit={(event) => {
              event.preventDefault();
              if (avisoDoCpf(cpf) === undefined && digitosDoCpf(cpf) !== '') {
                buscar.mutate();
              }
            }}
          >
            <div className="flex-1">
              <Field
                label="Digite o CPF do tomador"
                required
                value={cpf}
                maxLength={14}
                inputMode="numeric"
                placeholder="Digite aqui"
                error={avisoCpf}
                onChange={(event) => {
                  setCpf(event.target.value);
                  setConferido(null);
                }}
              />
            </div>
            <Button
              type="submit"
              variant="secondary"
              loading={buscar.isPending}
              disabled={digitosDoCpf(cpf) === '' || avisoCpf !== undefined}
            >
              Prosseguir
            </Button>
          </form>

          {buscar.isError ? (
            <Alert tone="danger" title="Nao foi possivel buscar o CPF">
              {buscar.error.message}
            </Alert>
          ) : null}
          {conferido && !conferido.valid ? (
            <Alert tone="danger" title="CPF invalido">
              {conferido.message}
            </Alert>
          ) : null}
          {conferido && conferido.valid && conferido.document_kind !== 'cpf' ? (
            <Alert tone="warning" title="Digite um CPF">
              A cotacao de pessoa juridica e a Nova cotacao Garantia.
            </Alert>
          ) : null}
          {conferido &&
          conferido.valid &&
          conferido.document_kind === 'cpf' &&
          !conferido.individual_enabled ? (
            <Alert tone="warning" title="Pessoa fisica nao habilitada">
              {conferido.message}
            </Alert>
          ) : null}
          {conferido &&
          conferido.valid &&
          conferido.registered &&
          conferido.policyholder === null ? (
            <Alert tone="warning" title="Tomador de outra corretora">
              Este CPF ja esta cadastrado, e o tomador e atendido por outra corretora.
            </Alert>
          ) : null}

          {rascunho.policyholder_id ? (
            <div className="flex flex-col gap-3 rounded-md border border-line p-3">
              <p className="text-sm text-ink-800">
                Tomador:{' '}
                <span className="font-medium">{rascunho.policyholder_label}</span> (
                {formatCpfCnpj(rascunho.policyholder_document)})
              </p>
              <div>
                <p className="mb-1 text-xs font-semibold text-ink-700">
                  Dados do leiloeiro
                </p>
                {erroDoLeiloeiro ? (
                  <Alert
                    tone="warning"
                    title="Tomador cadastrado sem os dados do leiloeiro"
                  >
                    Os dados do leiloeiro nao foram gravados: {erroDoLeiloeiro} Grave a
                    UF e a matricula de novo aqui.
                  </Alert>
                ) : null}
                <DadosDoLeiloeiro
                  policyholderId={rascunho.policyholder_id}
                  podeEditar={podeGravarLeiloeiro}
                />
              </div>
            </div>
          ) : null}
        </CardBody>
      </Card>

      {naoCadastrado ? (
        <Card>
          <CardHeader
            title="Tomador nao cadastrado"
            description="Nao encontramos o CPF buscado. Preencha os dados abaixo para solicitar o cadastro."
          />
          <CardBody>
            {can('tomadores.tomador.create') ? (
              <form
                className="flex flex-col gap-4"
                noValidate
                onSubmit={(event) => {
                  event.preventDefault();
                  setTentouCadastrar(true);
                  if (
                    nome.trim() === '' ||
                    avisoDoTelefone(telefone) !== undefined ||
                    matriculaSemUf
                  )
                    return;
                  cadastrar.mutate();
                }}
              >
                {cadastrar.isError ? (
                  <Alert tone="danger" title="Nao foi possivel cadastrar o tomador">
                    {cadastrar.error.message}
                  </Alert>
                ) : null}
                <Field
                  label="Nome completo"
                  required
                  value={nome}
                  error={
                    (tentouCadastrar && nome.trim() === ''
                      ? 'O nome completo e obrigatorio.'
                      : undefined) ?? fieldErrorOf(erroDoCadastro, 'legal_name')
                  }
                  onChange={(event) => setNome(event.target.value)}
                />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    label="Telefone"
                    required
                    value={telefone}
                    placeholder="Digite o telefone"
                    error={avisoTelefone ?? fieldErrorOf(erroDoCadastro, 'phone')}
                    onChange={(event) => setTelefone(event.target.value)}
                  />
                  <Field
                    label="E-mail"
                    type="email"
                    value={email}
                    placeholder="Digite o e-mail"
                    error={fieldErrorOf(erroDoCadastro, 'email')}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </div>
                <p className="text-xs font-semibold text-ink-700">Dados do leiloeiro</p>
                {podeGravarLeiloeiro ? (
                  <>
                    <CamposDoLeiloeiro
                      uf={uf}
                      matricula={matricula}
                      onUf={setUf}
                      onMatricula={setMatricula}
                      apiError={erroDoCadastro}
                      obrigatorio={false}
                    />
                    {tentouCadastrar && matriculaSemUf ? (
                      <Alert tone="warning" title="Informe a UF da junta comercial">
                        A matricula so e gravada com a UF da junta comercial em que o
                        leiloeiro e matriculado.
                      </Alert>
                    ) : null}
                  </>
                ) : (
                  <Alert tone="info" title="Dados do leiloeiro">
                    O seu perfil cadastra o tomador, mas os dados do leiloeiro pedem a
                    permissao de alterar o tomador. O cadastro segue sem eles; peca a
                    quem administra a corretora para grava-los.
                  </Alert>
                )}
                <div className="flex justify-end">
                  <Button type="submit" loading={cadastrar.isPending}>
                    Prosseguir com o cadastro
                  </Button>
                </div>
              </form>
            ) : (
              <Alert tone="info" title="Cadastro de tomador">
                O seu perfil nao cadastra tomador. Peca o cadastro a quem administra a
                corretora.
              </Alert>
            )}
          </CardBody>
        </Card>
      ) : null}

      <Alert tone="info" title="Documentos para cadastro">
        A lista de documentos exigidos do tomador pessoa fisica e o envio pela corretora
        estao pendentes de regra:
        <ListaDePendencias opcoes={opcoes} codigos={['CRD-017']} />
      </Alert>
      <Alert tone="info" title="Limites e Taxas do tomador">
        O limite e a taxa do tomador pessoa fisica dependem dos criterios de credito de
        pessoa fisica, que nao tem regra aprovada. A consulta de credito e o motor sao
        de pessoa juridica e ficam bloqueados para o CPF; a analise e manual, da
        seguradora.
        <ListaDePendencias opcoes={opcoes} codigos={['K4']} />
      </Alert>
    </div>
  );
}

/**
 * Passo "Dados do segurado" da pessoa fisica (NCT-199 e NCT-200): a UF e a junta
 * comercial da UF, com os dados cadastrais e o endereco que a seguradora cadastrou.
 * Sem junta na UF, "Junta comercial sem dados na base" (NCT-200, regra sem fonte).
 */
export function SeguradoPfStep({
  rascunho,
  alterar,
  opcoes,
}: PassoPfProps & { opcoes: IndividualQuoteOptions }) {
  const ufs = useUfs();
  const uf = rascunho.registry_state;
  const juntas = useQuery({
    queryKey: ['commercial-registries', uf],
    queryFn: ({ signal }) => brokerQuotes.commercialRegistries(uf, signal),
    enabled: uf !== '',
  });
  const lista = juntas.data ?? [];
  const escolhida = lista.find((junta) => junta.id === rascunho.junta_id);

  function escolher(id: string) {
    const junta = lista.find((item) => item.id === id);
    const endereco = junta?.addresses.find((e) => e.is_main) ?? junta?.addresses[0];
    alterar({
      junta_id: junta?.id ?? '',
      junta_label: junta?.legal_name ?? '',
      junta_document: junta?.document ?? '',
      junta_address_label: endereco?.label ?? '',
    });
  }

  // Com uma junta so na UF, ela fica escolhida, como o preenchimento automatico do
  // OnPoint.
  const unica = lista.length === 1 ? lista[0] : undefined;
  useEffect(() => {
    if (!unica || rascunho.junta_id === unica.id) return;
    const endereco = unica.addresses.find((e) => e.is_main) ?? unica.addresses[0];
    alterar({
      junta_id: unica.id,
      junta_label: unica.legal_name,
      junta_document: unica.document,
      junta_address_label: endereco?.label ?? '',
    });
  }, [unica, rascunho.junta_id, alterar]);

  return (
    <Card>
      <CardHeader
        title="Dados do Segurado"
        description="O segurado da cotacao de pessoa fisica e a junta comercial da UF."
      />
      <CardBody className="flex flex-col gap-4">
        <Select
          label="UF da junta comercial"
          required
          placeholder="Selecione a UF"
          value={uf}
          options={(ufs.data ?? []).map((estado) => ({
            value: estado.code,
            label: `${estado.code} - ${estado.name}`,
          }))}
          onChange={(event) =>
            alterar({
              registry_state: event.target.value,
              junta_id: '',
              junta_label: '',
              junta_document: '',
              junta_address_label: '',
            })
          }
        />
        {uf !== '' && juntas.isPending ? (
          <p className="text-sm text-ink-600">
            Carregando informacoes da junta comercial
          </p>
        ) : null}
        {juntas.isError ? (
          <Alert tone="danger" title="Erro inesperado">
            {juntas.error.message}
          </Alert>
        ) : null}
        {juntas.isSuccess && lista.length === 0 ? (
          <Alert tone="warning" title="Junta comercial sem dados na base">
            A seguradora ainda nao cadastrou a junta comercial desta UF como segurado.
            <ListaDePendencias opcoes={opcoes} codigos={['NCT-200']} />
          </Alert>
        ) : null}
        {lista.length > 1 ? (
          <Select
            label="Junta comercial"
            placeholder="Selecione a junta"
            value={rascunho.junta_id}
            options={lista.map((junta) => ({
              value: junta.id,
              label: junta.legal_name,
            }))}
            onChange={(event) => escolher(event.target.value)}
          />
        ) : null}
        {escolhida ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1 text-sm">
              <p className="text-xs font-semibold text-ink-700">Dados cadastrais</p>
              <p className="text-ink-900">{escolhida.legal_name}</p>
              <p className="tabular-nums text-ink-600">
                {formatCpfCnpj(escolhida.document)}
              </p>
              {escolhida.email ? (
                <p className="text-ink-600">{escolhida.email}</p>
              ) : null}
              {escolhida.blocked ? (
                <p className="text-2xs text-warning-700">
                  Segurado bloqueado. O efeito do bloqueio nao tem fonte (SEG-006).
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-1 text-sm">
              <p className="text-xs font-semibold text-ink-700">Endereco</p>
              <p className="text-ink-900">{rascunho.junta_address_label}</p>
            </div>
            <p className="text-2xs text-ink-500 sm:col-span-2">
              Os dados cadastrais sao preenchidos automaticamente conforme a Junta
              Comercial selecionada.
            </p>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

/**
 * Passo "Dados do risco" da pessoa fisica (NCT-201 e NCT-202): a modalidade de
 * leiloeiro, a importancia segurada da junta (pendente de regra), a vigencia e o
 * objeto. O prazo e o fim da vigencia sao digitados: a conversao entre eles e PRC-008.
 */
export function RiscoPfStep({
  rascunho,
  alterar,
  opcoes,
}: PassoPfProps & { opcoes: IndividualQuoteOptions }) {
  return (
    <Card>
      <CardHeader
        title="Dados do risco"
        description="Modalidade de leiloeiro, importancia segurada e vigencia."
      />
      <CardBody className="flex flex-col gap-4">
        {opcoes.modalities.length === 0 ? (
          <Alert tone="warning" title="Nenhuma modalidade de leiloeiro ativa">
            A seguradora marca a modalidade de leiloeiro na configuracao da modalidade.
            <ListaDePendencias opcoes={opcoes} codigos={['NCT-201']} />
          </Alert>
        ) : null}
        <Select
          label="Modalidade"
          required
          placeholder="Leiloeiro"
          value={rascunho.modality_id}
          options={opcoes.modalities.map((modalidade) => ({
            value: modalidade.id,
            label: `${modalidade.name} (${modalidade.product.label})`,
          }))}
          onChange={(event) => alterar({ modality_id: event.target.value })}
        />
        <div className="flex flex-col gap-1">
          <p className="text-xs font-semibold text-ink-700">
            Importancia Segurada (Valor definido conforme a Junta Comercial selecionada)
          </p>
          <p className="rounded-md border border-line bg-ink-50 px-3 py-2 text-sm text-ink-700">
            Pendente de regra (NCT-202): o valor da garantia por junta comercial nao tem
            fonte aprovada.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="Inicio da vigencia"
            type="date"
            required
            value={rascunho.coverage_start}
            onChange={(event) => alterar({ coverage_start: event.target.value })}
          />
          <Field
            label="Prazo em dias"
            inputMode="numeric"
            placeholder="Digite a quantidade de dias"
            value={rascunho.term_days}
            // NCT-115: o prazo fora da vigencia da modalidade so avisa (SUB-004).
            hint={
              avisosDoPrazo(
                opcoes.modalities.find((item) => item.id === rascunho.modality_id),
                rascunho.term_days,
              ).join(' ') || undefined
            }
            onChange={(event) =>
              alterar({ term_days: event.target.value.replace(/\D/g, '') })
            }
          />
          <Field
            label="Fim da vigencia"
            type="date"
            value={rascunho.coverage_end}
            onChange={(event) => alterar({ coverage_end: event.target.value })}
          />
        </div>
        <Textarea
          label="Objeto da apolice"
          rows={3}
          maxLength={2000}
          value={rascunho.subject}
          onChange={(event) => alterar({ subject: event.target.value })}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <p className="text-xs font-semibold text-ink-700">Taxa aplicada</p>
            <p className="text-sm text-ink-600">
              Pendente: depende da importancia segurada (NCT-202).
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-xs font-semibold text-ink-700">Premio</p>
            <p className="text-sm text-ink-600">
              Pendente: depende da importancia segurada (NCT-202).
            </p>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

/** Aviso do bloqueio da geracao, com o motivo e as pendencias do servidor. */
export function BloqueioDaGeracao({ opcoes }: { opcoes: IndividualQuoteOptions }) {
  return (
    <Alert tone="warning" title="A cotacao de pessoa fisica nao pode ser gerada">
      {opcoes.block_reason}
      <ListaDePendencias opcoes={opcoes} codigos={opcoes.blocked_rules} />
    </Alert>
  );
}
